﻿import $ from 'jquery';
import moment from 'moment';

export class StringExtensions {
  static isNullOrWhitespace(value: string) {
    return value == null || value.trim() == "";
  }

  static toBoolean(value: string): boolean {
    if (StringExtensions.isNullOrWhitespace(value))
      return false;

    return value.trim().toLowerCase() === 'true' ? true : false;
  }
}

export class DateExtensions {
  static equals(date1: Date, date2: Date): boolean {
    return moment(date1).isSame(date2, 'day');
  }

  static addDays(value: Date, days: number): Date {
    return moment(value).add(days, 'day').toDate();
  }

  static addMonths(value: Date, months: number): Date {
    return moment(value).add(months, 'month').toDate();
  }

  static toFieldDateString(value: Date) {
    if (!value.isValid())
      return "";
    return moment(value).format('MM/DD/YYYY');
  }

  static isValid(value: Date): boolean {
    return !isNaN(value.getTime());
  }
}

export interface IRustigEvent<T = any> extends Event {
  data: T;
}

export class NodeExtensions {
  static trigger<T = any>(node: Node, eventName: string, data?: T, bubbles: boolean = true, cancelable: boolean = true): boolean {
    let event = document.createEvent('Event') as IRustigEvent<T>;
    event.data = data || {} as any;
    event.initEvent(eventName, bubbles, cancelable);
    return node.dispatchEvent(event);
  }
}

export class HTMLElementExtensions {
  // https://github.com/jquery/jquery/blob/master/src/css/hiddenVisibleSelectors.js
  static isVisible(element: HTMLElement): boolean {
    return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  }
}

declare global {

  interface Window {
    isWindowUnloading: boolean;
  }

  /* JQuery */
  interface JQuery {
    copyFieldBooleanAttributes($target: JQuery);
  }

  interface String {
    capitalize(): string;
    sentenceCase(): string;

    /**
     * Encloses the string in brackets
     * @param {string | number} value - If provided, will prepend ="value" to the closing bracket
     */
    asAttrSelector(value?: string | number): string;

    /**
     * Prepends "data-" to the string
     */
    asData(): string;

    /**
     * Prepending the string with "data-" and then encloses that value with brackets
     * @param {string | number} value? - If provided, will prepend ="value" to the closing bracket
     */
    asJsSelector(): string;
    asDataSelector(value?: string | number): string;
    isNullOrWhitespace(): boolean;
    toBoolean(): boolean;
    endsWith(value: string, length?: number): boolean;
  }

  interface Date {
    equals(date: Date): boolean;
    addDays(days: number): Date;
    addMonths(months: number): Date;
    toFieldDateString(): string;
    isValid(): boolean;
  }

  interface Node {
    trigger<T = any>(eventName: string, data?: T, bubbles?: boolean, cancelable?: boolean): boolean;
  }

  interface HTMLElement {
    isVisible(): boolean;
  }
}

/* String */

String.prototype.isNullOrWhitespace = function () {
  return StringExtensions.isNullOrWhitespace(this);
}

String.prototype.toBoolean = function () {
  return StringExtensions.toBoolean(this);
}

String.prototype.asJsSelector = function () {
  if (this === undefined)
    return undefined;

  return "js".asDataSelector(this.toString());
}

String.prototype.capitalize = function () {
  return this.charAt(0).toUpperCase() + this.slice(1);
}

String.prototype.sentenceCase = function () {
  return this.charAt(0).toUpperCase() + this.slice(1).toLowerCase();
}

String.prototype.asAttrSelector = function (value?: string | number) {
  if (arguments.length === 0)
    return '[' + this + ']';
  else if (arguments.length === 1 && (typeof (arguments[0]) === 'string' || typeof (arguments[0]) === 'number'))
    return '[' + this + '="' + value + '"]';
  else
    return this;
}

String.prototype.asData = function () {
  return 'data-' + this;
}

String.prototype.asDataSelector = function (value?: string | number) {
  if (arguments.length === 0)
    return this.asData().asAttrSelector();
  else if (arguments.length === 1 && (typeof (arguments[0]) === 'string' || typeof (arguments[0]) === 'number'))
    return this.asData().asAttrSelector(arguments[0]);
  else
    return this;
}

/* Window */

$.fn.copyFieldBooleanAttributes = function ($target) {
  let $this = $(this);
  let attributes = ['readonly', 'disabled', 'data-required'];

  for (var i = 0; i < attributes.length; i++) {
    if ($this.attr(attributes[i]))
      $target.attr(attributes[i], $this.attr(attributes[i]));
  }
}

/* Date */

Date.prototype.equals = function (date) {
  return DateExtensions.equals(this, date);
}

Date.prototype.addDays = function (days) {
  return DateExtensions.addDays(this, days);
}

Date.prototype.addMonths = function (months) {
  return DateExtensions.addMonths(this, months);
}

Date.prototype.toFieldDateString = function () {
  return DateExtensions.toFieldDateString(this);
}

Date.prototype.isValid = function () {
  return DateExtensions.isValid(this);
}

/* HTMLElement */

HTMLElement.prototype.isVisible = function () {
  return HTMLElementExtensions.isVisible(this);
}

/* Node */

Node.prototype.trigger = function (eventName: string, data?: any, bubbles?: boolean, cancelable?: boolean) {
  return NodeExtensions.trigger(this, eventName, data, bubbles, cancelable);
}